
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import AsyncButton from "../common/AsyncButton";
import { useAsyncAction } from "../common/useAsyncAction";


const Login = () => {
  const [loginData, setLoginData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const { run, loading } = useAsyncAction();

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData({ ...loginData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!loginData.username.trim()) {
      newErrors.username = 'Username/Email is required';
    }
    if (!loginData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const { login } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState({ open: false, message: "", severity: "error" })

  const handleClose = () => {
    setState({ ...state, open: false });
  };

  const handleLogin = async e => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    await run(async () => {
      try {
        const username = loginData.username.trim();
        const password = loginData.password;
        const resp = await login(username, password);
        
        if (resp === 0) {
          navigate("/app");
        } else if (resp === 1) {
          setState({ open: true, message: "Incorrect Username/Email", severity: "error" });
        } else if (resp === 2) {
          setState({ open: true, message: "Incorrect Password", severity: "error" });
        } else if (typeof resp === "object" && resp?.code === 4) {
          setState({
            open: true,
            message: resp.message || "Too many wrong password attempts. Please try again after 24 hours or use Forgot Password.",
            severity: "error"
          });
        } else if (typeof resp === "object" && resp?.code === 5) {
          setState({
            open: true,
            message: resp.message || "Your account is not activated yet",
            severity: "error"
          });
        } else if (resp === 3) {
          setState({ open: true, message: "Internal error please retry", severity: "error" });
        }
      } catch (error) {
        setState({ open: true, message: "An unexpected error occurred. Please try again.", severity: "error" });
      }
    });
  };

  return (
    <>
      <form>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={state.open}
          onClose={handleClose}
          key={state.message}
          TransitionProps={{ timeout: 300 }}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 2,
            top: { xs: 72, sm: 80 },
          }}
        >
          <Alert 
            variant="filled" 
            severity={state.severity}
            sx={{
              animation: 'slideUp 0.3s ease-out',
              '@keyframes slideUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(-20px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {state.message}
          </Alert>
        </Snackbar>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          sx={{ 
            mb: 3,
            animation: 'fadeIn 0.5s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Welcome!
          </Typography>
        </Box>
        <TextField
          label="Username/Email"
          variant="outlined"
          fullWidth
          name="username"
          value={loginData.username}
          onChange={handleLoginChange}
          required
          autoComplete="username"
          error={!!errors.username}
          helperText={errors.username}
          disabled={loading}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
            },
          }}
          aria-label="Username or Email input"
        />
        <TextField
          type={showPassword ? 'text' : 'password'}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? 'hide the password' : 'display the password'
                    }
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          label="Password"
          name="password"
          onChange={handleLoginChange}
          required
          fullWidth
          error={!!errors.password}
          helperText={errors.password}
          disabled={loading}
          autoComplete="current-password"
          sx={{ mb: 2 }}
          aria-label="Password input"
        />
        <Box display="flex" justifyContent="center" alignItems="center">
          <AsyncButton 
            type="submit" 
            variant="contained" 
            color="primary" 
            onClick={handleLogin}
            loading={loading}
            spinnerSize={24}
            sx={{ minWidth: 120 }}
            aria-label="Login button"
          >
            Login
          </AsyncButton>
        </Box>
      </form>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Button sx={{ textTransform: "initial" }} type="link" color="primary" onClick={(e) => { e.preventDefault(); navigate("/forgotpassword") }}>
          Forgot Password?
        </Button>
      </Box>
    </>
  );
};

export default Login;
